// Shared between DataTable v2 Excel export and the reports ExcelJS export.
// Owns the `ExcelExportType` → `numFmt` + coercion table so both paths can't
// drift. A "typed" cell writes a native number/date/currency/link value with a
// matching `numFmt`; a "text" cell writes a plain string pinned to the `@`
// text format (Excel then leaves identifiers like "FV/04/2026", "0012" and
// NIPs exactly as written instead of re-coercing them to dates or numbers).

export type ExcelExportType = "number" | "currency" | "weight" | "date" | "text" | "link";

export interface TypedCellInput {
  type: ExcelExportType;
  /** Raw value pre-resolved by the caller. number | string | Date | null | undefined. */
  value: unknown;
  /**
   * For `currency`: 3-letter ISO code. When absent or not a 3-letter code the
   * cell is still written as a native number, but with a currency-less
   * `#,##0.00` format — the app never asserts a currency it can't resolve.
   */
  currency?: string | null;
  /** For `link`: absolute URL. Without it a `link` cell degrades to plain text. */
  hyperlink?: string | null;
  /** Override the per-type default numFmt. */
  numFmt?: string;
  /**
   * When the resolver produced no value, hand the cell to the caller's text
   * fallback instead of writing it blank. Opt-in, because for most columns an
   * absent value genuinely means an empty cell. Set it where the typed reading
   * is only defined for *some* rows — a multi-leg transport price has no single
   * number, and blanking it would drop what the cell visibly shows.
   */
  fallbackWhenEmpty?: boolean;
}

export const DEFAULT_NUMFMT: Record<ExcelExportType, string> = {
  number: "#,##0",
  currency: "#,##0.00",
  weight: '#,##0.000" t"',
  date: "yyyy-mm-dd",
  text: "@",
  link: "@",
};

/**
 * The `number` format for a value that actually has decimals.
 *
 * It can't be the default, because in an Excel number format the `.` is a
 * literal: Excel emits the decimal separator whether or not any `#` after it
 * resolves to a digit — `#` suppresses the digits, never the separator. Under
 * `#,##0.##` a whole number therefore renders as "-2700.", which is what an
 * operator reported from a real workbook. So the format is picked per cell (see
 * `numberNumFmt`) and `DEFAULT_NUMFMT.number` holds the whole-number form.
 *
 * `currency` and `weight` are immune — their `0` placeholders force the digits.
 */
export const NUMBER_DECIMAL_NUMFMT = "#,##0.##";

/**
 * Pick the `number` numFmt for one value. Thousands separator either way, and
 * the stored value is untouched either way — this only decides what Excel
 * paints.
 *
 * The test is "does this DISPLAY as whole at two decimals", not
 * `Number.isInteger`: `#,##0.##` shows at most two places, so 0.001 would round
 * away to nothing and land back on the dangling separator.
 */
export function numberNumFmt(n: number): string {
  const atTwoPlaces = Math.round(n * 100) / 100;
  return Number.isInteger(atTwoPlaces) ? DEFAULT_NUMFMT.number : NUMBER_DECIMAL_NUMFMT;
}

/** Matches a bare calendar date — no instant, no timezone. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A date numFmt carrying an hour token renders a time-of-day, not just a day. */
const HOUR_TOKEN_RE = /h/i;

/**
 * Build the numFmt for a currency cell. A resolvable 3-letter ISO code gets the
 * `[$CODE]` token; anything else falls back to a plain 2-decimal number so the
 * value stays summable without claiming a currency the row doesn't carry.
 */
export function currencyNumFmt(code: string | null | undefined): string {
  const iso = typeof code === "string" ? code.trim().toUpperCase() : "";
  return /^[A-Z]{3}$/.test(iso) ? `#,##0.00 [$${iso}]` : DEFAULT_NUMFMT.currency;
}

function toDate(v: unknown): Date | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === "number") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Normalise a raw date value into the `Date` ExcelJS should serialise.
 *
 * ExcelJS converts a `Date` to a workbook serial as a pure UTC quantity —
 * `25569 + d.getTime() / 86400000` (exceljs/lib/utils/utils.js) — with no
 * timezone adjustment. So the *instant* we hand it decides which day (and which
 * time) Excel renders, and the three input shapes we receive need different
 * treatment to land on the day the operator saw in the UI:
 *
 *  - `"2026-08-12"` — a bare calendar date. No instant is involved at all, so
 *    pin it straight to UTC midnight. (`new Date("2026-08-12")` already does
 *    this, but doing it explicitly keeps the intent from depending on the
 *    engine's ISO-parsing rules.)
 *  - a local-midnight `Date` (what dayjs and every AntD date picker produce) —
 *    in UTC+2 this is `2026-08-11T22:00Z`, which serialises as the *previous*
 *    day. Re-pin the value's LOCAL calendar day to UTC midnight.
 *  - a full ISO timestamp with a date-only format — the UI renders its local
 *    calendar day (`useFormat().date` → dayjs, local), so take that same local
 *    day and pin it to UTC midnight.
 *
 * With `withTime` (a `yyyy-mm-dd hh:mm`-style format) the time of day matters:
 * shift the instant by the local offset so Excel's UTC-based rendering shows
 * the same wall-clock the UI shows.
 */
export function toExcelDate(v: unknown, withTime = false): Date | null {
  if (v == null || v === "") return null;

  if (typeof v === "string") {
    const m = DATE_ONLY_RE.exec(v.trim());
    if (m) {
      const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  const parsed = toDate(v);
  if (parsed == null) return null;

  if (withTime) {
    return new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  }
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

function toNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toCellText(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "object") return JSON.stringify(v) ?? "";
  return String(v as number | bigint | boolean | symbol);
}

/** Minimal cell surface we need. Matches ExcelJS's `Cell`. */
export interface ExcelCellLike {
  value: unknown;
  numFmt: string;
}

/**
 * Apply a typed cell input to an ExcelJS cell. Returns true on success.
 * Returns false when the raw value can't be coerced to the declared type —
 * the caller should then fall back to string extraction. A null/empty raw
 * value is written as an empty cell and still counts as success, except for
 * `link` (see the guard below).
 */
export function applyTypedCell(cell: ExcelCellLike, input: TypedCellInput): boolean {
  const { type, value } = input;
  if (value == null || value === "") {
    // A `link` with no readable text means "we couldn't derive it", not "this
    // cell is empty" — an EntityLink's label is a ReactNode, and only strings
    // and numbers survive the trip here. Refusing hands the cell to the
    // caller's fallback, which reads the rendered text (and yields an empty
    // string when the cell really is empty). Every other type treats an absent
    // value as a genuinely empty cell — unless the column opted in to
    // `fallbackWhenEmpty`, which says the same thing for its own reason.
    if (type === "link" || input.fallbackWhenEmpty) return false;
    cell.value = null;
    return true;
  }
  switch (type) {
    case "number":
    case "weight": {
      const n = toNumber(value);
      if (n == null) return false;
      cell.value = n;
      cell.numFmt =
        input.numFmt ?? (type === "number" ? numberNumFmt(n) : DEFAULT_NUMFMT.weight);
      return true;
    }
    case "currency": {
      const n = toNumber(value);
      if (n == null) return false;
      cell.value = n;
      cell.numFmt = input.numFmt ?? currencyNumFmt(input.currency);
      return true;
    }
    case "date": {
      const numFmt = input.numFmt ?? DEFAULT_NUMFMT.date;
      const d = toExcelDate(value, HOUR_TOKEN_RE.test(numFmt));
      if (d == null) return false;
      cell.value = d;
      cell.numFmt = numFmt;
      return true;
    }
    case "link": {
      const text = toCellText(value);
      const href = typeof input.hyperlink === "string" ? input.hyperlink.trim() : "";
      // Without a target this is just a label — write it as text rather than
      // an ExcelJS hyperlink cell pointing nowhere.
      cell.value = href ? { text, hyperlink: href } : text;
      cell.numFmt = input.numFmt ?? DEFAULT_NUMFMT.link;
      return true;
    }
    case "text": {
      cell.value = toCellText(value);
      cell.numFmt = input.numFmt ?? DEFAULT_NUMFMT.text;
      return true;
    }
    default:
      return false;
  }
}
